import axios from 'axios';
import auth from '@client/components/auth';
import { debounce, isEmpty } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { Async, toAsyncFetched } from '@client/new/helpers/types';

export type ExerciseSuggestion = {
  id: string;
  name: string;
  description: string;
};

type ExerciseSearchResult = {
  exercise_id: string;
  exercise_name: string;
  exercise_description: string;
};

const mapSearchResult = (result: ExerciseSearchResult): ExerciseSuggestion => ({
  id: result.exercise_id,
  name: result.exercise_name,
  description: result.exercise_description,
});

const joinUrl = (base: string, path: string) =>
  `${base.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;

const getSearchApiUrl = () => {
  if (import.meta.env.DEV) return '/search-api';
  const url = import.meta.env.VITE_SEARCH_API_URL;
  return typeof url === 'string' && url.length > 0 ? url : null;
};

const fetchExerciseSuggestions = (
  query: string,
  signal?: AbortSignal,
): Promise<{ data: { results: ExerciseSearchResult[] } }> => {
  const baseUrl = getSearchApiUrl();
  if (!baseUrl) {
    return Promise.reject(new Error('VITE_SEARCH_API_URL is not configured'));
  }

  return axios.post<{ results: ExerciseSearchResult[] }>(
    joinUrl(baseUrl, '/exercise'),
    { query },
    { headers: { Authorization: auth.getToken() }, signal },
  );
};

export const useExerciseSuggestionsAsync = (
  query: string,
): Async<ExerciseSuggestion[]> => {
  const [state, setState] = useState<Async<ExerciseSuggestion[]>>({
    type: 'not-fetched',
  });
  const lastQueryRef = useRef<string>('');
  const abortRef = useRef<AbortController | null>(null);

  const debouncedSearch = useRef(
    debounce((searchQuery: string) => {
      lastQueryRef.current = searchQuery;

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      fetchExerciseSuggestions(searchQuery, controller.signal)
        .then((response) => {
          if (lastQueryRef.current !== searchQuery) return;

          setState(toAsyncFetched(response.data.results.map(mapSearchResult)));
        })
        .catch(() => {
          if (lastQueryRef.current !== searchQuery) return;
          if (controller.signal.aborted) return;

          setState({
            type: 'fetch-error',
            value: 'Could not fetch suggestions',
          });
        });
    }, 250),
  ).current;

  useEffect(() => {
    debouncedSearch.cancel();
    abortRef.current?.abort();

    if (isEmpty(query)) {
      lastQueryRef.current = '';
      setState({ type: 'not-fetched' });
      return;
    }

    setState({ type: 'fetching' });
    debouncedSearch(query);

    return () => {
      debouncedSearch.cancel();
      abortRef.current?.abort();
    };
  }, [debouncedSearch, query]);

  return state;
};

export const useExerciseSuggestions = (query: string): ExerciseSuggestion[] => {
  const [suggestions, setSuggestions] = useState<ExerciseSuggestion[]>([]);
  const abortRef = useRef<AbortController | null>(null);

  const debouncedSearch = useRef(
    debounce((searchQuery: string) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      fetchExerciseSuggestions(searchQuery, controller.signal)
        .then((response) => {
          if (controller.signal.aborted) return;
          setSuggestions(response.data.results.map(mapSearchResult));
        })
        .catch(() => {
          if (controller.signal.aborted) return;
          setSuggestions([]);
        });
    }, 250),
  ).current;

  useEffect(() => {
    debouncedSearch.cancel();
    abortRef.current?.abort();

    if (isEmpty(query)) {
      setSuggestions([]);
      return;
    }

    debouncedSearch(query);

    return () => {
      debouncedSearch.cancel();
      abortRef.current?.abort();
    };
  }, [debouncedSearch, query]);

  return suggestions;
};
